// Catching a dataset's copy up with the server: one `sync` call from the
// copy's seq (and rev), applied to a new copy.
//
// Promise:
// - The copy starts from IndexedDB when the screen holds none yet (shown at
//   once through `onLocal`), then asks only `since` its seq: an unchanged
//   dataset costs one request answering `{ seq }`.
// - A `reset` replaces every row with the snapshot's (fetched from its URL and
//   unzipped here), then applies the operations after it. Rows the server no
//   longer has never stay behind.
// - `more` asks again from where the answer stopped, until it is caught up.
// - Nothing changed: the same copy object comes back (no redraw).
// - Rows are kept oldest write first (`_at`); a row written again moves last.
// - A dataset that is gone or no longer seen (404) is forgotten here too.
import {
  compareByWrite,
  type DatasetDTO,
  type DatasetRow,
  type DatasetSync,
} from "@trameria/core/datasets-shared";
import { type Api, ApiError } from "../data/api";
import type { DatasetCopies } from "./copies";

export interface DatasetCopy {
  id: string;
  seq: number;
  rev: number;
  dto: DatasetDTO | null;
  /** By `_id`, oldest write first. Never changed in place: a sync makes a new map. */
  rows: ReadonlyMap<string, DatasetRow>;
}

export interface DatasetSyncDeps {
  api: Api;
  copies: DatasetCopies;
  /** Reads a snapshot's rows from its URL (default: fetch and gunzip). */
  fetchSnapshot?(url: string): Promise<DatasetRow[]>;
}

function rowMap(rows: Iterable<DatasetRow>): Map<string, DatasetRow> {
  return new Map([...rows].sort(compareByWrite).map((r) => [r._id, r]));
}

/**
 * A snapshot's rows: gzip NDJSON, the first line its meta. The bucket answers
 * `Access-Control-Allow-Origin` for the app; no cookies go with it.
 */
export async function fetchSnapshotRows(url: string): Promise<DatasetRow[]> {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok || !res.body) throw new Error(`snapshot ${res.status}`);
  const text = await new Response(
    res.body.pipeThrough(new DecompressionStream("gzip")),
  ).text();
  const rows: DatasetRow[] = [];
  for (const [i, line] of text.split("\n").entries())
    if (i > 0 && line) rows.push(JSON.parse(line) as DatasetRow);
  return rows;
}

const syncPath = (id: string, since: number, rev: number) =>
  `/api/datasets/${encodeURIComponent(id)}/sync?since=${since}&rev=${rev}`;

export async function syncDataset(
  deps: DatasetSyncDeps,
  id: string,
  held: DatasetCopy | undefined,
  onLocal?: (copy: DatasetCopy) => void,
): Promise<DatasetCopy> {
  let start = held;
  if (!start) {
    const stored = await deps.copies.load(id).catch(() => null);
    if (stored) {
      start = {
        id,
        seq: stored.meta.seq,
        rev: stored.meta.rev,
        dto: stored.meta.dto,
        rows: rowMap(stored.rows),
      };
      onLocal?.(start);
    }
  }
  let seq = start?.seq ?? 0;
  let rev = start?.rev ?? 0;
  let dto = start?.dto ?? null;
  let rows: Map<string, DatasetRow> | null = null;
  let replaced = false;
  const put = new Map<string, DatasetRow>();
  const deleted = new Set<string>();
  const own = () => {
    rows ??= new Map(start?.rows ?? []);
    return rows;
  };
  try {
    for (;;) {
      const answer = await deps.api<DatasetSync>(syncPath(id, seq, rev));
      if (answer.meta) {
        dto = answer.meta;
        rev = answer.meta.rev;
      }
      if (answer.reset) {
        const fresh = answer.reset.url
          ? await (deps.fetchSnapshot ?? fetchSnapshotRows)(answer.reset.url)
          : [];
        rows = rowMap(fresh);
        replaced = true;
        put.clear();
        deleted.clear();
      }
      for (const op of answer.ops ?? []) {
        const map = own();
        if ("put" in op) {
          map.delete(op.put._id);
          map.set(op.put._id, op.put);
          put.set(op.put._id, op.put);
          deleted.delete(op.put._id);
        } else {
          map.delete(op.delete);
          put.delete(op.delete);
          deleted.add(op.delete);
        }
      }
      seq = answer.seq;
      if (!answer.more) break;
    }
  } catch (error) {
    if (error instanceof ApiError && error.status === 404)
      await deps.copies.remove(id).catch(() => {});
    throw error;
  }
  if (start && !rows && seq === start.seq && rev === start.rev) return start;
  const next: DatasetCopy = {
    id,
    seq,
    rev,
    dto,
    rows: rows ?? start?.rows ?? new Map(),
  };
  const meta = { seq, rev, dto };
  void deps.copies
    .save(
      id,
      replaced
        ? { meta, replace: next.rows.values() }
        : { meta, put: put.values(), delete: deleted },
    )
    .catch(() => {});
  return next;
}
